import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';

export type Role = 'RECEPTION' | 'HOUSEKEEPING' | 'MANAGER' | 'ADMIN';

export interface CurrentUser {
  id: string;
  username: string;
  fullName: string;
  role: Role;
  properties: string[];
  currentPropertyId: string | null;
  mustChangePassword: boolean;
}

export interface PropertyOption {
  id: string;
  name: string;
  code: string;
}

// --- API calls (don't use api.ts to avoid circular import) ---

async function authFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json as T;
}

export const authApi = {
  me: () => authFetch<CurrentUser>('/api/auth/me'),

  login: (username: string, password: string) =>
    authFetch<CurrentUser>('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    }),

  logout: () =>
    authFetch<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    authFetch<{ ok: boolean }>('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  listMyProperties: () => authFetch<PropertyOption[]>('/api/properties'),

  switchProperty: (propertyId: string) =>
    authFetch<CurrentUser>('/api/auth/switch-property', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ propertyId }),
    }),
};

// --- Context ---

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchProperty: (propertyId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const u = await authApi.login(username, password);
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const switchProperty = useCallback(async (propertyId: string) => {
    const updated = await authApi.switchProperty(propertyId);
    setUser(updated);
    // Full page reload so all data refetches under the new property context
    window.location.reload();
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, logout, switchProperty }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
